import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { PrintLabelService } from '../../service/api/print-label-service';
import { ShipmentService } from '../../service/api/shipment-service';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';

const modalStyle = {
  width: '100%',
  maxWidth: '500px',
  height: 'auto',
  maxHeight: '90vh',
  paddingBottom: '2rem',
  overflow: 'auto',
};

const MappingsPrintPickTicketModal = ({
  show,
  onClose,
  onSubmit,
  shipmentData = null,
  userId = '',
  initialPrinter = '',
}) => {
  const [printer, setPrinter] = useState(initialPrinter);
  const [printers, setPrinters] = useState([]);
  const [printersLoading, setPrintersLoading] = useState(false);
  const [printersError, setPrintersError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const prevShowRef = useRef(false);
  const dispatch = useDispatch();
  


  useEffect(() => {
    // Only run when modal transitions from closed to open
    if (show && !prevShowRef.current) {

      setPrinter(initialPrinter);
      setPrintersLoading(true);
      setPrintersError('');
      setSubmitError('');
      PrintLabelService.getPrinters()
        .then(res => {
          if (res && res.status === 200 && Array.isArray(res.response)) {
            setPrinters(res.response);
            // Set first printer as default if none selected
            if (!initialPrinter && res.response.length > 0) {
              setPrinter(res.response[0].printerId.toString());
            } else if (initialPrinter) {
              setPrinter(initialPrinter);
            }
            
            // Ensure printer is always set if printers are available
            if (res.response.length > 0 && !printer) {
              setPrinter(res.response[0].printerId.toString());
            }
          } else {
            setPrinters([]);
            setPrintersError('Failed to load printers');
          }
        })
        .catch(() => {
          setPrinters([]);
          setPrintersError('Failed to load printers');
        })
        .finally(() => setPrintersLoading(false));
    }
    prevShowRef.current = show;
  }, [show, initialPrinter]);

  const handleSubmit = async () => {

    
    if (!printer || printer === '') {
      setSubmitError('Please select a printer.');
      return;
    }

    if (!shipmentData || !shipmentData.receivingShipmentID) {
      setSubmitError('Shipment data is missing.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      // Create pick ticket data (similar to PrintLabelResponse structure)
      const pickTicketData = {
        ShipmentId: shipmentData.receivingShipmentID,
        ShipmentNumber: shipmentData.shipmentNumber || '',
        ShipmentDate: shipmentData.shipmentDate || '',
        Comments: shipmentData.comments || '',
        VendorIds: shipmentData.vendorIds || [],
        MergedPOs: shipmentData.mergedPOs || [],
        POItems: shipmentData.poItems || []
      };
      
      const printJson = JSON.stringify(pickTicketData);
      
      // Create print request object for the Mobile/saveprintrequest API
      const printRequest = {
        requestId: 0,
        filePath: "",
        printerName: printer.toString(),
        requestedUser: userId,
        status: "",
        param1: shipmentData.receivingShipmentID.toString(),
        param2: shipmentData.shipmentNumber || '',
        param3: "",
        param4: "",
        param5: printJson,
        isAutoPrint: true,
        category: 'PORPickTicket'
      };
      

      
      // Call the Mobile/saveprintrequest API using PrintLabelService
      const response = await PrintLabelService.savePrintLabelRequest(printRequest);
      
      if (response.status === 200) {
        // Success - call onSubmit with the response data
        if (onSubmit) {
          onSubmit({ 
            printer, 
            printRequestId: response.response?.requestId || null,
            isReprint: false,
            message: 'Pick ticket print request submitted successfully!'
          });
        }
        
        dispatch(showToast({ 
          message: 'Pick ticket print request submitted successfully!', 
          type: 'success' 
        }));
        
        if (onClose) onClose();
      } else {
        setSubmitError(response?.errorMessage?.message || 'Failed to submit print request');
      }
    } catch (error) {

      setSubmitError('An error occurred while submitting the print request');
    } finally {
      setSubmitting(false);
    }
  };

  const isPrintDisabled = submitting || !printer;

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="md"
      centered
      dialogClassName="custom-mappings-print-pick-ticket-modal"
      contentClassName="custom-mappings-print-pick-ticket-modal-content"
    >
      <div style={modalStyle}>
        <Modal.Header closeButton>
          <Modal.Title>Print Pick Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="mb-2">
              <strong>Shipment Number:</strong> {shipmentData?.shipmentNumber || '-'}
            </div>
          </div>
          
          <Form.Group className="mb-3">
            <Form.Label>Printer</Form.Label>
            {printersLoading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" /> 
                Loading printers...
              </div>
            ) : printersError ? (
              <div className="text-danger">{printersError}</div>
            ) : (
              <Form.Select 
                value={printer} 
                onChange={e => {
                  setPrinter(e.target.value);
                }}
                isInvalid={!printer}
              >
                <option value="">Select Printer</option>
                {printers.map(p => {
                  return (
                    <option value={p.printerId.toString()} key={p.printerId}>
                      {p.printerName}
                    </option>
                  );
                })}
              </Form.Select>
            )}
          </Form.Group>
          
          {submitError && (
            <div className="alert alert-danger mt-3" role="alert">
              {submitError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ paddingBottom: '2rem' }}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPrintDisabled}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting Print Request...
              </>
            ) : (
              'Print Pick Ticket'
            )}
          </Button>
          {!submitting && isPrintDisabled && (
            <div className="text-danger ms-3">
              {!printer ? 'Please select a printer.' : ''}
            </div>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default MappingsPrintPickTicketModal;
